import { useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Film, MailCheck } from "lucide-react";
import Logo from "@/components/Logo";
import { toast } from "sonner";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [needsVerification, setNeedsVerification] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setNeedsVerification(false);
    try {
      const data = await login(email, password);
      toast.success(`Welcome back, ${data.name}`);
      const target = location.state?.from || (data.role === "admin" ? "/admin" : data.role === "filmmaker" ? "/filmmaker" : "/browse");
      navigate(target);
    } catch (err) {
      // 403 = email not verified yet. Show inline UI with a resend action
      // instead of just a generic toast so the user knows exactly what to do.
      if (err.response?.status === 403) {
        setNeedsVerification(true);
      } else {
        toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Login failed");
      }
    } finally {
      setLoading(false);
    }
  };

  const resendVerification = async () => {
    if (!email) {
      toast.error("Enter your email above first");
      return;
    }
    setResendLoading(true);
    try {
      await api.post("/auth/resend-verification", { email });
      toast.success("If that account exists and is unverified, we've sent a fresh link.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not resend verification email");
    } finally {
      setResendLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-ink grain flex items-center justify-center px-4 py-16">
      <div className="absolute top-6 left-6">
        <Link to="/" className="flex items-center group">
          <Logo size={32} />
        </Link>
      </div>

      <div className="w-full max-w-md bg-surface border border-white/10 rounded-2xl p-8 sm:p-10 shadow-2xl shadow-terracotta/5" data-testid="login-card">
        <div className="mb-8">
          <h1 className="font-display text-3xl font-black mb-2">Welcome back</h1>
          <p className="text-foreground/60 text-sm">Sign in to your Cinevoya account.</p>
        </div>

        <form onSubmit={submit} className="space-y-4" data-testid="login-form">
          {needsVerification && (
            <div className="p-4 rounded-lg border border-gold/30 bg-gold/10 space-y-3" data-testid="login-needs-verification">
              <div className="flex items-start gap-3">
                <MailCheck className="w-5 h-5 text-gold shrink-0 mt-0.5" />
                <div className="text-sm">
                  <p className="font-display font-bold text-gold">Confirm your email first</p>
                  <p className="text-foreground/75 mt-1">
                    We sent a confirmation link to <span className="text-foreground">{email}</span> when you signed up. Click it to activate your account, then come back here to sign in.
                  </p>
                </div>
              </div>
              <Button
                type="button"
                onClick={resendVerification}
                disabled={resendLoading}
                variant="outline"
                className="w-full border-gold/40 bg-transparent hover:bg-gold/15 text-gold hover:text-gold h-10 text-sm"
                data-testid="login-resend-verification-btn"
              >
                {resendLoading ? "Sending…" : "Resend confirmation email"}
              </Button>
            </div>
          )}
          <div>
            <Label htmlFor="email" className="text-foreground/80 text-sm">Email</Label>
            <Input
              id="email"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="mt-1.5 bg-ink border-white/10 h-11"
              data-testid="login-email-input"
            />
          </div>
          <div>
            <div className="flex items-center justify-between">
              <Label htmlFor="password" className="text-foreground/80 text-sm">Password</Label>
              <Link to="/forgot-password" className="text-xs text-terracotta hover:text-ember" data-testid="link-forgot-password">
                Forgot password?
              </Link>
            </div>
            <Input
              id="password"
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="mt-1.5 bg-ink border-white/10 h-11"
              data-testid="login-password-input"
            />
          </div>
          <Button type="submit" disabled={loading} className="w-full bg-terracotta hover:bg-ember text-white h-11" data-testid="login-submit-btn">
            {loading ? "Signing in…" : "Sign in"}
          </Button>
        </form>

        <p className="mt-6 text-sm text-center text-foreground/60">
          New to Cinevoya?{" "}
          <Link to="/register" className="text-terracotta hover:text-ember" data-testid="link-to-register">
            Create an account
          </Link>
        </p>
      </div>
    </div>
  );
}
